import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  BookOpen,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  Tag,
  Folder,
  FileText,
  ChevronLeft,
  ChevronRight,
  Eye,
  Check,
  X,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Label,
  Switch,
  Textarea,
} from '@/components/ui'
import { knowledgeApi, type CreateKnowledgeData, type UpdateKnowledgeData } from '@/api/endpoints/knowledge'
import type { KnowledgeEntry, KnowledgeFilters } from '@/types'
import { cn, formatDate } from '@/lib/utils'

const CONTENT_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'faq', label: 'FAQ' },
  { value: 'guide', label: 'Guide' },
  { value: 'policy', label: 'Policy' },
  { value: 'script', label: 'Script' },
  { value: 'product', label: 'Product Info' },
  { value: 'other', label: 'Other' },
]

const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'factoring', label: 'Factoring' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'process', label: 'Process' },
  { value: 'objections', label: 'Objections' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'general', label: 'General' },
]

const PAGE_SIZES = [10, 25, 50]

const getContentTypeColor = (type: string) => {
  switch (type) {
    case 'faq':
      return 'bg-blue-100 text-blue-700 border-blue-200'
    case 'guide':
      return 'bg-green-100 text-green-700 border-green-200'
    case 'policy':
      return 'bg-purple-100 text-purple-700 border-purple-200'
    case 'script':
      return 'bg-orange-100 text-orange-700 border-orange-200'
    case 'product':
      return 'bg-teal-100 text-teal-700 border-teal-200'
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200'
  }
}

const getContentTypeBadgeColor = (type: string) => {
  switch (type) {
    case 'faq':
      return 'bg-blue-500'
    case 'guide':
      return 'bg-green-500'
    case 'policy':
      return 'bg-purple-500'
    case 'script':
      return 'bg-orange-500'
    case 'product':
      return 'bg-teal-500'
    default:
      return 'bg-gray-500'
  }
}

export function KnowledgeBase() {
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<KnowledgeFilters>({
    page: 1,
    page_size: 25,
  })
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [contentTypeFilter, setContentTypeFilter] = useState<string>('all')
  const [activeFilter, setActiveFilter] = useState<string>('all')
  const [verifiedFilter, setVerifiedFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [selectedEntry, setSelectedEntry] = useState<KnowledgeEntry | null>(null)
  const [formData, setFormData] = useState<CreateKnowledgeData>({
    title: '',
    content: '',
    category: 'general',
    content_type: 'faq',
    tags: [],
    is_active: true,
    is_verified: false,
  })
  const [tagInput, setTagInput] = useState('')

  // Build filters for API
  const apiFilters = useMemo(() => {
    const f: KnowledgeFilters = {
      page: filters.page,
      page_size: filters.page_size,
    }
    if (categoryFilter !== 'all') f.category = categoryFilter
    if (contentTypeFilter !== 'all') f.content_type = contentTypeFilter
    if (activeFilter !== 'all') f.is_active = activeFilter === 'active'
    if (verifiedFilter !== 'all') f.is_verified = verifiedFilter === 'verified'
    if (searchQuery) f.search = searchQuery
    return f
  }, [filters, categoryFilter, contentTypeFilter, activeFilter, verifiedFilter, searchQuery])

  // Fetch knowledge entries
  const { data, isLoading, error } = useQuery({
    queryKey: ['knowledge', apiFilters],
    queryFn: () => knowledgeApi.getAll(apiFilters),
  })

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['knowledge-stats'],
    queryFn: () => knowledgeApi.getStats(),
  })

  const entries = data?.data?.items || []
  const totalPages = data?.data?.total_pages || 1
  const totalItems = data?.data?.total || 0
  const stats = statsData?.data

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateKnowledgeData) => knowledgeApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge'] })
      queryClient.invalidateQueries({ queryKey: ['knowledge-stats'] })
      setIsAddDialogOpen(false)
      resetForm()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateKnowledgeData }) => knowledgeApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge'] })
      queryClient.invalidateQueries({ queryKey: ['knowledge-stats'] })
      setIsEditDialogOpen(false)
      setSelectedEntry(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => knowledgeApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge'] })
      queryClient.invalidateQueries({ queryKey: ['knowledge-stats'] })
      setIsDeleteDialogOpen(false)
      setSelectedEntry(null)
    },
  })

  const verifyMutation = useMutation({
    mutationFn: (id: string) => knowledgeApi.verify(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge'] })
      queryClient.invalidateQueries({ queryKey: ['knowledge-stats'] })
    },
  })

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      category: 'general',
      content_type: 'faq',
      tags: [],
      is_active: true,
      is_verified: false,
    })
    setTagInput('')
  }

  const handleAddEntry = () => {
    createMutation.mutate(formData)
  }

  const handleEditEntry = () => {
    if (!selectedEntry) return
    updateMutation.mutate({
      id: selectedEntry.id,
      data: formData,
    })
  }

  const handleDeleteEntry = () => {
    if (!selectedEntry) return
    deleteMutation.mutate(selectedEntry.id)
  }

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()],
      }))
      setTagInput('')
    }
  }

  const handleRemoveTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(t => t !== tag) || [],
    }))
  }

  const openEditDialog = (entry: KnowledgeEntry) => {
    setSelectedEntry(entry)
    setFormData({
      title: entry.title,
      content: entry.content,
      category: entry.category,
      content_type: entry.content_type,
      tags: entry.tags || [],
      is_active: entry.is_active,
      is_verified: entry.is_verified,
    })
    setIsEditDialogOpen(true)
  }

  const openViewDialog = (entry: KnowledgeEntry) => {
    setSelectedEntry(entry)
    setIsViewDialogOpen(true)
  }

  const openDeleteDialog = (entry: KnowledgeEntry) => {
    setSelectedEntry(entry)
    setIsDeleteDialogOpen(true)
  }

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }))
  }

  const handlePageSizeChange = (size: string) => {
    setFilters(prev => ({ ...prev, page_size: parseInt(size), page: 1 }))
  }

  const clearFilters = () => {
    setCategoryFilter('all')
    setContentTypeFilter('all')
    setActiveFilter('all')
    setVerifiedFilter('all')
    setSearchQuery('')
    setFilters({ page: 1, page_size: 25 })
  }

  const hasActiveFilters = categoryFilter !== 'all' || contentTypeFilter !== 'all' || activeFilter !== 'all' || verifiedFilter !== 'all' || searchQuery

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Failed to load knowledge base. Please try again.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Knowledge Base</h1>
          <p className="text-muted-foreground">
            Manage knowledge entries for AI agents
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Entry
        </Button>
      </div>

      {/* Stats Summary */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BookOpen className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total || totalItems}</p>
                <p className="text-sm text-muted-foreground">Total Entries</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.active || entries.filter(e => e.is_active).length}</p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.verified || entries.filter(e => e.is_verified).length}</p>
                <p className="text-sm text-muted-foreground">Verified</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Folder className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {stats?.by_category ? Object.keys(stats.by_category).length : CATEGORIES.length - 1}
                </p>
                <p className="text-sm text-muted-foreground">Categories</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
            {/* Search */}
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search entries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Category Filter */}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Content Type Filter */}
            <Select value={contentTypeFilter} onValueChange={setContentTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {CONTENT_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Active Filter */}
            <Select value={activeFilter} onValueChange={setActiveFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>

            {/* Verified Filter */}
            <Select value={verifiedFilter} onValueChange={setVerifiedFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Verified" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="unverified">Unverified</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {hasActiveFilters && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {categoryFilter !== 'all' && (
                <Badge variant="secondary">{CATEGORIES.find(c => c.value === categoryFilter)?.label}</Badge>
              )}
              {contentTypeFilter !== 'all' && (
                <Badge variant="secondary">{CONTENT_TYPES.find(t => t.value === contentTypeFilter)?.label}</Badge>
              )}
              {activeFilter !== 'all' && (
                <Badge variant="secondary">{activeFilter === 'active' ? 'Active' : 'Inactive'}</Badge>
              )}
              {verifiedFilter !== 'all' && (
                <Badge variant="secondary">{verifiedFilter === 'verified' ? 'Verified' : 'Unverified'}</Badge>
              )}
              {searchQuery && <Badge variant="secondary">Search: {searchQuery}</Badge>}
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear all
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Knowledge Entries List */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Entries
              <Badge variant="outline" className="ml-2">{totalItems} total</Badge>
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Show:</span>
              <Select
                value={String(filters.page_size)}
                onValueChange={handlePageSizeChange}
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map(size => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="flex gap-4 animate-pulse p-4 border rounded-lg">
                  <div className="w-10 h-10 bg-muted rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-1/3" />
                    <div className="h-3 bg-muted rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium text-lg">No entries found</h3>
              <p className="text-muted-foreground">
                {hasActiveFilters
                  ? 'Try adjusting your filters'
                  : 'Create your first knowledge entry to get started'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {entries.map(entry => (
                <div
                  key={entry.id}
                  className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => openViewDialog(entry)}
                >
                  {/* Icon */}
                  <div className={cn('p-2 rounded-lg', getContentTypeColor(entry.content_type))}>
                    <FileText className="h-5 w-5" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="font-medium">{entry.title}</p>
                      <Badge className={cn('text-white text-xs', getContentTypeBadgeColor(entry.content_type))}>
                        {entry.content_type}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        <Folder className="h-3 w-3 mr-1" />
                        {entry.category}
                      </Badge>
                      {entry.is_verified ? (
                        <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
                          <XCircle className="h-3 w-3 mr-1" />
                          Unverified
                        </Badge>
                      )}
                      <Badge variant={entry.is_active ? 'default' : 'secondary'}>
                        {entry.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    {/* Content Preview */}
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {entry.content}
                    </p>

                    {/* Tags */}
                    {entry.tags && entry.tags.length > 0 && (
                      <div className="flex items-center gap-1 mt-2 flex-wrap">
                        <Tag className="h-3 w-3 text-muted-foreground" />
                        {entry.tags.slice(0, 5).map(tag => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                        {entry.tags.length > 5 && (
                          <span className="text-xs text-muted-foreground">+{entry.tags.length - 5} more</span>
                        )}
                      </div>
                    )}

                    {/* Date */}
                    <p className="text-xs text-muted-foreground mt-2">
                      Updated {formatDate(entry.updated_at)}
                    </p>
                  </div>

                  {/* Actions */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openViewDialog(entry); }}>
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditDialog(entry); }}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      {!entry.is_verified && (
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); verifyMutation.mutate(entry.id); }}>
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Mark Verified
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); openDeleteDialog(entry); }}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Page {filters.page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! - 1)}
                  disabled={filters.page === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! + 1)}
                  disabled={filters.page === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Entry Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedEntry && (
            <>
              <DialogHeader>
                <div className={cn(
                  'flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg text-white',
                  getContentTypeBadgeColor(selectedEntry.content_type)
                )}>
                  <FileText className="h-6 w-6" />
                  <div>
                    <DialogTitle className="text-white">{selectedEntry.title}</DialogTitle>
                    <p className="text-sm opacity-90 capitalize">
                      {selectedEntry.content_type} - {selectedEntry.category}
                    </p>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4">
                {/* Status badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  {selectedEntry.is_verified ? (
                    <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
                      <XCircle className="h-3 w-3 mr-1" />
                      Unverified
                    </Badge>
                  )}
                  <Badge variant={selectedEntry.is_active ? 'default' : 'secondary'}>
                    {selectedEntry.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>

                {/* Tags */}
                {selectedEntry.tags && selectedEntry.tags.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedEntry.tags.map(tag => (
                        <Badge key={tag} variant="secondary">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Content */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Content</p>
                  <div className="p-4 bg-muted rounded-lg text-sm whitespace-pre-wrap">
                    {selectedEntry.content}
                  </div>
                </div>

                {/* Dates */}
                <div className="flex justify-between text-xs text-muted-foreground pt-4 border-t">
                  <span>Created: {formatDate(selectedEntry.created_at)}</span>
                  <span>Updated: {formatDate(selectedEntry.updated_at)}</span>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
                  Close
                </Button>
                <Button onClick={() => { setIsViewDialogOpen(false); openEditDialog(selectedEntry); }}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Entry Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-green-600 text-white">
              <Plus className="h-6 w-6" />
              <DialogTitle className="text-white">Add Knowledge Entry</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="e.g., What is freight factoring?"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.filter(c => c.value !== 'all').map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="content_type">Content Type</Label>
                <Select
                  value={formData.content_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, content_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPES.filter(t => t.value !== 'all').map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Content *</Label>
              <Textarea
                id="content"
                placeholder="Enter the knowledge content..."
                value={formData.content}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                rows={8}
              />
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a tag"
                  value={tagInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTagInput(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddTag()
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={handleAddTag}>
                  Add
                </Button>
              </div>
              {formData.tags && formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="gap-1">
                      {tag}
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-destructive"
                        onClick={() => handleRemoveTag(tag)}
                      />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="is_verified"
                  checked={formData.is_verified}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, is_verified: checked }))}
                />
                <Label htmlFor="is_verified">Verified</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddEntry}
              disabled={!formData.title || !formData.content || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-blue-600 text-white">
              <Pencil className="h-6 w-6" />
              <DialogTitle className="text-white">Edit Knowledge Entry</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title *</Label>
              <Input
                id="edit-title"
                placeholder="e.g., What is freight factoring?"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.filter(c => c.value !== 'all').map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-content_type">Content Type</Label>
                <Select
                  value={formData.content_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, content_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPES.filter(t => t.value !== 'all').map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-content">Content *</Label>
              <Textarea
                id="edit-content"
                placeholder="Enter the knowledge content..."
                value={formData.content}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                rows={8}
              />
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a tag"
                  value={tagInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTagInput(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddTag()
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={handleAddTag}>
                  Add
                </Button>
              </div>
              {formData.tags && formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="gap-1">
                      {tag}
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-destructive"
                        onClick={() => handleRemoveTag(tag)}
                      />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  id="edit-is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
                <Label htmlFor="edit-is_active">Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="edit-is_verified"
                  checked={formData.is_verified}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, is_verified: checked }))}
                />
                <Label htmlFor="edit-is_verified">Verified</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEditEntry}
              disabled={!formData.title || !formData.content || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Entry Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-red-600 text-white">
              <Trash2 className="h-6 w-6" />
              <DialogTitle className="text-white">Delete Knowledge Entry</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <p>Are you sure you want to delete this entry?</p>
            {selectedEntry && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium">{selectedEntry.title}</p>
                <p className="text-sm text-muted-foreground capitalize">
                  {selectedEntry.content_type} - {selectedEntry.category}
                </p>
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              This action cannot be undone.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteEntry}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default KnowledgeBase
